import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { currencySymbol } from "./format";
import { amountRule, parseThresholdDraft, pickImpactRule, pickRiseRule, type ImpactRule, type MatrixSplits, type MatrixThresholds, type RiseRule } from "./focusMatrix";
import type { Currency } from "./types";

const RISE_OPTIONS: ReadonlyArray<{ kind: RiseRule["kind"]; label: string; title: string }> = [
  { kind: "cpi", label: "CPI", title: "Inflation over the window, or the scope average when inflation is unavailable" },
  { kind: "scope", label: "Scope average", title: "The average rise of the whole scope" },
  { kind: "pct", label: "Set %", title: "A rise you type" },
];
const IMPACT_OPTIONS: ReadonlyArray<{ kind: ImpactRule["kind"]; label: string; title: string }> = [
  { kind: "mean", label: "Mean", title: "The mean impact of the points shown" },
  { kind: "amount", label: "Set amount", title: "An amount you type" },
];
const NO_SCOPE_INDEX = "No scope index for this selection: the basket is empty";
const CAPTION = "text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground";
const UNIT = "text-sm text-muted-foreground";

/** A typed value commits only when it parses. The draft keeps what the person is typing while it reads as the drawn value ("5.", "5.125"), and shows the drawn value again on blur (an empty, unreadable or below-min box). */
function NumberDraft({ value, label, min, step, onCommit }: { value: number; label: string; min?: number; step: string; onCommit: (next: number) => void }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft((current) => parseThresholdDraft(current, min) === value ? current : String(value)), [min, value]);
  return <Input data-nonmutating type="number" inputMode="decimal" aria-label={label} className="h-8 w-24" value={draft} min={min} step={step} onBlur={() => setDraft(String(value))} onChange={(event) => {
    const text = event.target.value;
    setDraft(text);
    const next = parseThresholdDraft(text, min);
    if (next != null) onCommit(next);
  }} />;
}

export function MatrixThresholdControls({ thresholds, drawn = thresholds, split, currency, scopeAvailable, onChange }: {
  /** The rule pair as stored (from the URL). Every change is built from it, so a rule the matrix cannot draw right now (Scope average on an empty basket) is kept. */
  thresholds: MatrixThresholds;
  /** The rule pair as drawn (`drawnThresholds`): the pressed button and the shown box follow it. Defaults to `thresholds`. */
  drawn?: MatrixThresholds;
  split: MatrixSplits;
  currency: Currency | null;
  /** False when the scope has no finite index: the Scope average button is disabled. */
  scopeAvailable: boolean;
  onChange: (next: MatrixThresholds) => void;
}) {
  const { rise, impact } = drawn;

  return <div data-threshold-controls className="flex flex-wrap items-end gap-3">
    <div className="grid gap-1">
      <span className={CAPTION}>Rise line</span>
      <div className="flex items-center gap-2">
        <div role="group" aria-label="Rise line" className="flex rounded-md border border-border p-0.5">
          {RISE_OPTIONS.map((option) => {
            const blocked = option.kind === "scope" && !scopeAvailable;
            return <Button key={option.kind} data-nonmutating size="sm" variant={rise.kind === option.kind ? "secondary" : "ghost"} aria-pressed={rise.kind === option.kind} disabled={blocked} title={blocked ? NO_SCOPE_INDEX : option.title} onClick={() => onChange(pickRiseRule(thresholds, option.kind, split))}>{option.label}</Button>;
          })}
        </div>
        {rise.kind === "pct" && <><NumberDraft value={rise.value} label="Rise line percent" step="0.1" onCommit={(value) => onChange({ ...thresholds, rise: { kind: "pct", value } })} /><span className={UNIT}>%</span></>}
      </div>
    </div>
    <div className="grid gap-1">
      <span className={CAPTION}>Impact line</span>
      <div className="flex items-center gap-2">
        <div role="group" aria-label="Impact line" className="flex rounded-md border border-border p-0.5">
          {IMPACT_OPTIONS.map((option) => <Button key={option.kind} data-nonmutating size="sm" variant={impact.kind === option.kind ? "secondary" : "ghost"} aria-pressed={impact.kind === option.kind} title={option.title} onClick={() => onChange(pickImpactRule(thresholds, option.kind, split, currency))}>{option.label}</Button>)}
        </div>
        {impact.kind === "amount" && <>{currency && <span className={UNIT}>{currencySymbol(currency)}</span>}<NumberDraft value={impact.value} label={`Impact line amount${currency ? ` (${currency})` : ""}`} min={0} step="1" onCommit={(value) => onChange({ ...thresholds, impact: amountRule(value, currency) })} /></>}
      </div>
    </div>
  </div>;
}
